import React, { Fragment, forwardRef, useImperativeHandle, useRef } from 'react'
import { InventoryCheck } from '../entities/InventoryCheck'
import { Grid, Paper, TextField } from '@mui/material'
import { Field, Form, Formik, FormikProps } from 'formik'
import * as Yup from 'yup'
import { updateInventoryCheck } from '../api'
import { utcIntialDate } from '../../../utils/dateHelper'
import { DateTime } from 'luxon'
import { Filter } from '../../../components/_WMS/FilterComponent'

interface EditInventoryCheckModalProps {
  inventoryCheck: InventoryCheck
  handleSnackbar: (message: string, success: boolean) => void
  handleCloseModal: () => void
  requestInventoryCheck: (
    filters?: Filter[],
    page?: number,
    pageSize?: number
  ) => void
}

export interface EditInventoryCheckModalRef {
  submit: () => void
}

export const validationSchema = Yup.object().shape({
  LPN: Yup.string()
    .trim()
    .min(1, 'Se requiere al menos un caracter')
    .required('LPN/Tote es obligatorio')
})

export const EditInventoryCheckModal = forwardRef<
  EditInventoryCheckModalRef,
  EditInventoryCheckModalProps
>(({
  inventoryCheck,
  handleSnackbar,
  handleCloseModal,
  requestInventoryCheck
}, ref) => {
  const formikRef = useRef<FormikProps<any>>(null)

  useImperativeHandle(ref, () => ({
    submit: () => {
      formikRef.current?.submitForm()
    }
  }))

  const handleSubmit = async (values: any) => {
    const finalValues: InventoryCheck = {
      ...values,
      promiseDate: utcIntialDate(values.promiseDate as string)
    }

    await updateInventoryCheck(values.inventoryCheckId, finalValues)
      .then(() => {
        requestInventoryCheck()
        handleSnackbar('Entidad editada correctamente', true)
        handleCloseModal()
      })
      .catch((error) => {
        handleSnackbar('Error al editar: ' + error, false)
      })
  }

  // se combierte la fecha, al formato aceptado por el componente
  const convertToDateInputFormat = (
    dateTime: string | Date | undefined
  ): string => {
    if (!dateTime) {
      return ''
    }

    const date =
      typeof dateTime === 'string'
        ? DateTime.fromISO(dateTime) // Parsear desde un ISO string
        : DateTime.fromJSDate(dateTime) // Parsear desde un objeto Date

    return date.toFormat('yyyy-MM-dd') // Convertir al formato YYYY-MM-DD
  }

  const transformedInventoryCheck = {
    ...inventoryCheck,
    promiseDate: convertToDateInputFormat(inventoryCheck.promiseDate)
  }

  return (
    <Fragment>
      <Formik
        innerRef={formikRef}
        initialValues={transformedInventoryCheck}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            await handleSubmit(values)
          } catch (error) {
          } finally {
            setSubmitting(false)
          }
        }}
      >
        {() => (
          <Form autoComplete='off'>
            <Paper variant='outlined' style={{ backgroundColor: 'transparent', borderRadius: 8, padding: '12px 16px', marginBottom: 0 }}>
            <Grid container direction='row' spacing={2}>
              <Grid item xs={2}>
                <Field name='inventoryCheckId'>
                  {({ field }: any) => (
                    <TextField
                      {...field}
                      fullWidth
                      label='ID'
                      variant='outlined'
                      InputProps={{ readOnly: true }}
                    />
                  )}
                </Field>
              </Grid>
              <Grid item xs={5}>
                <Field name='tag'>
                  {({ field }: any) => (
                    <TextField
                      {...field}
                      fullWidth
                      label='Tag'
                      variant='outlined'
                    />
                  )}
                </Field>
              </Grid>
              <Grid item xs={5}>
                <Field name='promiseDate'>
                  {({ field }: any) => (
                    <TextField
                      {...field}
                      fullWidth
                      label='Fecha de Compromiso'
                      type='date'
                      variant='outlined'
                      InputLabelProps={{
                        shrink: true
                      }}
                    />
                  )}
                </Field>
              </Grid>
            </Grid>
            </Paper>
          </Form>
        )}
      </Formik>
    </Fragment>
  )
})
